import React, { Dispatch, useMemo, useState } from 'react';

import { useUser } from 'controllers/contexts/user';
import {
  BRAND_CENTER_SAVED_STEP_KEY,
  INFINITY_TESTING_TRIED_STEP_KEY,
  PS_APP_PROG_ONBOARDING_MANAGED_FEATURE_KEY,
  PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY,
} from 'providers/ProgressiveOnboarding/constants/constants';
import {
  managedSteps,
  touchlessSteps,
} from 'providers/ProgressiveOnboarding/constants/groupedSteps';
import {
  useGetOnboardingStates,
  useMarkStepCompleted,
} from 'providers/ProgressiveOnboarding/queries';
import {
  createGuide,
  getMappedFeatureKey,
  incompleteGuides,
} from 'providers/ProgressiveOnboarding/utils';
import { createSafeContext } from 'utils/createSafeContext';

import { BCC } from 'components/admin/utils/feature-flags';
import { useInfinityTestingBillingStatus } from 'components/billing/modules/plans/hooks/useInfinityTestingBillingStatus';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import type {
  Guide,
  IMarkStepCompleted,
} from 'providers/ProgressiveOnboarding/types';

interface ProgressiveOnboardingContext {
  /** All available guides - have not been completed */
  availableGuides: Guide[];

  /** Currently active guide - open in the side drawer */
  activeGuide?: Guide;

  /** Function to set active guide */
  setActiveGuide: Dispatch<Guide | undefined>;

  markStepCompleted: ({
    guideKey,
    stepKey,
    metadata,
  }: IMarkStepCompleted) => void;

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  error: unknown;
}

export const [Provider, useProgressiveOnboarding] =
  createSafeContext<ProgressiveOnboardingContext>(
    'ProgressiveOnboardingContext',
    'useProgressiveOnboarding must be a descendant of ProgressiveOnboardingProvider',
  );

interface ProgressiveOnboardingProviderProps {
  children?: React.ReactNode;
}

export const ProgressiveOnboardingProvider = ({
  children,
}: ProgressiveOnboardingProviderProps) => {
  const { data: onboardingStates, isLoading, error } = useGetOnboardingStates();
  const { mutate: markStepCompleted } = useMarkStepCompleted();
  const { hasLabsFlag } = usePSLabs();
  const isEligibleForBrandCenter = hasLabsFlag(BCC);
  const { isEligible: isEligibleForInfinityTesting } =
    useInfinityTestingBillingStatus();
  const {
    user: { is_cs_led: isCSLed },
  } = useUser();

  // The following logic in availableGuides remaps the featureKey for PS_APP_PROG_ONBOARDING_FEATURE_KEY
  // to either PS_APP_PROG_ONBOARDING_MANAGED_FEATURE_KEY or PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY
  // based on the isCSLed flag, ensuring the correct onboarding flow is shown for the user type.
  const availableGuides = useMemo(
    () =>
      onboardingStates?.filter(incompleteGuides).map((state) => {
        const mappedFeatureKey = getMappedFeatureKey(
          state.featureKey,
          isCSLed ?? false,
        );

        // Filter states based on the guide type
        const stepFilterMap: Record<string, readonly string[]> = {
          [PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY]: touchlessSteps,
          [PS_APP_PROG_ONBOARDING_MANAGED_FEATURE_KEY]: managedSteps,
        };

        const allowedSteps = stepFilterMap[mappedFeatureKey];
        let filteredStates = allowedSteps
          ? state.states.filter((step) => allowedSteps.includes(step.stepKey))
          : state.states;

        if (!isEligibleForBrandCenter) {
          filteredStates = filteredStates.filter(
            (step) => step.stepKey !== BRAND_CENTER_SAVED_STEP_KEY,
          );
        }
        if (!isEligibleForInfinityTesting) {
          filteredStates = filteredStates.filter(
            (step) => step.stepKey !== INFINITY_TESTING_TRIED_STEP_KEY,
          );
        }

        return createGuide({
          ...state,
          featureKey: mappedFeatureKey,
          states: filteredStates,
        });
      }) ?? [],
    [
      onboardingStates,
      isCSLed,
      isEligibleForInfinityTesting,
      isEligibleForBrandCenter,
    ],
  );

  const [activeGuide, setActiveGuide] = useState<Guide>();

  return (
    <Provider
      value={{
        availableGuides,
        activeGuide,
        setActiveGuide,
        markStepCompleted,
        isLoading,
        error,
      }}
    >
      {children}
    </Provider>
  );
};
